'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Divider } from '@common/components';
import type { ReactNode } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image, PageNavigation } from '@/components/client';
import { CenteredDetailTemplate } from '@/components/template/CenteredDetailTemplate';
import { useUserAgentMobile } from '@/hooks/useUserAgentMobile';
import { useDebugStore } from '@/stores/DebugStore';
import { getStaticCdnUrl } from '@/utils';
import { DonationEventUrlNormalizer } from '../../_components/DonationEventUrlNormalizer';
import { KB_EVENT, MONTHLY_DATES } from './constants';
const DONATION_EVENT_IMAGES = {
  heroHeading: getStaticCdnUrl('kb-card-event/hero-heading-2608.png'),
  heroCards: getStaticCdnUrl('kb-card-event/hero-cards.png'),
  taxBenefit: getStaticCdnUrl('kb-card-event/tax-benefit.png'),
  giftBenefit: getStaticCdnUrl('kb-card-event/gift-benefit.png'),
  taxGuideBox: getStaticCdnUrl('kb-card-event/tax-guide-box.png'),
  step2BenefitsBox: getStaticCdnUrl('kb-card-event/step2-benefits-box-2608.png')
} as const;
const CardApplyButton = t0 => {
  const $ = _c(2);
  const {
    href,
    isMobile
  } = t0;
  const t1 = isMobile ? href.mobile : href.pc;
  let t2;
  if ($[0] !== t1) {
    t2 = <div className="flex justify-center"><Button asChild={true} theme="sub-fill" className="w-250 rounded-small bg-[#12b8f5] text-Gray-white hover:bg-[#0d9fd4]"><CustomLink href={t1} target="_blank" rel="noopener noreferrer">카드 신청하기</CustomLink></Button></div>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
};
const SectionTitle = t0 => {
  const $ = _c(7);
  const {
    num,
    title
  } = t0;
  let t1;
  if ($[0] !== num) {
    t1 = <span className="font-600 text-[#12b8f5] typo-h7" aria-hidden="true">{num}</span>;
    $[0] = num;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <h2 className="font-600 text-Gray-black typo-h4">{title}</h2>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex flex-col items-center gap-4 text-center">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const DonationHeroSection = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <PageNavigation isNotUsedHomeButton={true} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <section className="bg-[linear-gradient(180deg,#edf7fc_0%,#bdeafa_100%)]"><div className="mx-auto w-full max-w-[750px]">{t0}<div className="mt-70 flex flex-col items-center gap-28 px-20 pb-40 text-center"><Image src={DONATION_EVENT_IMAGES.heroHeading} alt={"Wello\uC640 KB\uAD6D\uBBFC\uCE74\uB4DC \uC774\uBCA4\uD2B8 \uD5E4\uB354"} width={672} height={350} className="h-auto w-full max-w-[336px]" preload={true} /><Image src={DONATION_EVENT_IMAGES.heroCards} alt={"KB\uAD6D\uBBFC\uCE74\uB4DC \uB300\uC0C1 \uCE74\uB4DC"} width={552} height={748} className="h-auto w-full max-w-[276px]" preload={true} /></div></div></section>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const StandardHeroSection = t0 => {
  const $ = _c(3);
  const {
    mainImage
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PageNavigation isNotUsedHomeButton={true} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== mainImage) {
    t2 = <section className="bg-[linear-gradient(180deg,#E6F9FF_48.63%,#B3E9FA_79.93%)]"><div className="mx-auto w-full max-w-[750px]">{t1}<Image src={mainImage} alt={"KB\uAD6D\uBBFC\uCE74\uB4DC \uCCB4\uD06C\uCE74\uB4DC \uC2E0\uADDC \uBC1C\uAE09 \uC774\uBCA4\uD2B8"} width={850} height={600} className="w-full" preload={true} fetchPriority="high" /></div></section>;
    $[1] = mainImage;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const StandardStep2Content = t0 => {
  const $ = _c(2);
  const {
    text
  } = t0;
  let t1;
  if ($[0] !== text) {
    t1 = <p className="whitespace-pre-line text-center font-500 text-Gray-black typo-body3">{text}</p>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const DonationStep2Content = t0 => {
  const $ = _c(8);
  const {
    text,
    subText
  } = t0;
  let t1;
  if ($[0] !== text) {
    t1 = <p className="font-500 text-Gray-black typo-body3">{text}</p>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== subText) {
    t2 = <p className="whitespace-pre-line font-500 text-Gray-black typo-body3">{subText}</p>;
    $[2] = subText;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Image src={DONATION_EVENT_IMAGES.step2BenefitsBox} alt={"\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80 \uCC38\uC5EC \uD61C\uD0DD \uC548\uB0B4"} width={622} height={200} className="h-auto w-full max-w-[311px]" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2) {
    t4 = <div className="flex flex-col items-center gap-12 text-center">{t1}{t2}{t3}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const DonationInfoSection = () => {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <h2 className="font-700 text-Gray-black typo-h4">고향사랑기부제란?</h2>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex w-full max-w-[317px] flex-col items-center gap-24 text-center"><div className="flex flex-col gap-16">{t0}<div className="flex flex-col gap-16 text-[#48515d] typo-body3"><p className="whitespace-pre-line">고향사랑기부제는 지방소멸 위기 대응과{"\n"}지역 균형발전을 위해 도입된 국가정책이에요.</p><p className="whitespace-pre-line">자신의 주소지를 제외한 다른 지자체에 기부하면{"\n"}해당 지역 복지·문화·청년정책 등에 활용돼요.</p><p className="whitespace-pre-line">기부자는 10만원까지 100% 세액공제 혜택과 함께{"\n"}기부금의 30% 만큼 답례품을 받을 수 있어요.</p></div></div><Image src={DONATION_EVENT_IMAGES.taxGuideBox} alt={"\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80 \uC138\uC561\uACF5\uC81C \uC548\uB0B4"} width={554} height={320} className="h-auto w-full max-w-[277px]" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h2 className="whitespace-pre-line font-700 text-Gray-black typo-h4">고향사랑기부제에 참여하면,{"\n"}13만원 혜택이!</h2>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Image src={DONATION_EVENT_IMAGES.taxBenefit} alt={"10\uB9CC\uC6D0 \uC138\uC561\uACF5\uC81C"} width={144} height={144} className="h-auto w-72" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex min-h-[176px] flex-col items-center justify-center gap-16 rounded-xxlarge bg-Gray-white px-12 py-20 shadow-[0_14px_40px_rgba(18,184,245,0.08)]">{t3}<div className="text-center text-Gray-black typo-body4"><p>10만원 기부하면,</p><p>10만원 세액공제</p></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Image src={DONATION_EVENT_IMAGES.giftBenefit} alt={"3\uB9CC\uC6D0 \uC0C1\uB2F9 \uD504\uB9AC\uBBF8\uC5C4 \uB2F5\uB840\uD488"} width={144} height={144} className="h-auto w-72" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <section className="bg-[#bdeafa] px-24 py-60"><div className="mx-auto flex w-full max-w-[750px] flex-col items-center gap-40">{t1}<div className="flex w-full max-w-[326px] flex-col items-center gap-20 text-center">{t2}<div className="grid w-full grid-cols-2 gap-16">{t4}<div className="flex min-h-[176px] flex-col items-center justify-center gap-16 rounded-xxlarge bg-Gray-white px-12 py-20 shadow-[0_14px_40px_rgba(18,184,245,0.08)]">{t5}<div className="text-center text-Gray-black typo-body4"><p>3만원 상당</p><p>프리미엄 답례품</p></div></div></div></div></div></section>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
};
type EventVariant = 'standard' | 'donation';
type EventContent = (typeof KB_EVENT)['content'];
const VARIANT_CONFIG: Record<EventVariant, {
  renderHero: (content: EventContent) => ReactNode;
  renderInfo: () => ReactNode;
  renderStep2: (content: EventContent) => ReactNode;
}> = {
  standard: {
    renderHero: content => <StandardHeroSection mainImage={content.mainImage} />,
    renderInfo: () => null,
    renderStep2: content => <StandardStep2Content text={content.step2Text} />
  },
  donation: {
    renderHero: () => <DonationHeroSection />,
    renderInfo: () => <DonationInfoSection />,
    renderStep2: content => <DonationStep2Content text={content.step2Text} subText={content.step2SubText} />
  }
};
export const KBCardEventPage = () => {
  const isMobile = useUserAgentMobile();
  const {
    kbEventDateIndex
  } = useDebugStore();
  const overrideDates = kbEventDateIndex !== null ? MONTHLY_DATES[kbEventDateIndex] : null;
  const event = overrideDates ? {
    ...KB_EVENT,
    variant: overrideDates.variant,
    bannerLines: overrideDates.bannerLines,
    period: {
      issueText: overrideDates.issueText,
      useText: overrideDates.useText
    },
    eligibility: {
      conditions: [KB_EVENT.eligibility.conditions[0], KB_EVENT.eligibility.conditions[1], `${overrideDates.withdrawalDeadlineText} 이전 체크카드 탈회 후\n이벤트 기간 중 신규 발급 회원`]
    },
    content: {
      ...KB_EVENT.content,
      step2Text: overrideDates.step2Text,
      step2SubText: overrideDates.step2SubText
    },
    benefit: {
      ...KB_EVENT.benefit,
      deadline: overrideDates.deadline,
      description: overrideDates.benefitDescription
    },
    notices: {
      ...KB_EVENT.notices,
      complianceText: overrideDates.complianceText
    }
  } : KB_EVENT;
  const variantConfig = VARIANT_CONFIG[event.variant];
  return <CenteredDetailTemplate fullWidth>
      <DonationEventUrlNormalizer />
      <article className="flex flex-col">
        {variantConfig.renderHero(event.content)}

        <section className="bg-[#12b8f5] px-24 py-32" aria-label="이벤트 안내 배너">
          <div className="mx-auto flex w-full max-w-[750px] flex-col items-center gap-4 text-center">
            <p className="font-700 text-Gray-white typo-h7">
              신규 발급하고 대상 카드로
            </p>
            {event.bannerLines.map(line => <p key={line} className="font-700 text-Gray-black typo-h7">
                {line}
              </p>)}
          </div>
        </section>

        {variantConfig.renderInfo()}

        <section className="bg-Gray-white px-32 py-60">
          <div className="mx-auto flex w-full max-w-[750px] flex-col items-center gap-60">
            <div className="flex w-full flex-col items-center gap-24">
              <SectionTitle num="01" title="이벤트 기간" />
              <div className="flex w-full flex-col gap-16 rounded-small p-20">
                <div className="gap-8 flex-center">
                  <span className="shrink-0 font-500 text-Gray-black typo-body3">
                    [발급]
                  </span>
                  <span className="text-center font-500 text-Gray-600 typo-body3">
                    {event.period.issueText}
                  </span>
                </div>
                <div className="gap-8 flex-center">
                  <span className="shrink-0 font-500 text-Gray-black typo-body3">
                    [이용]
                  </span>
                  <span className="text-center font-500 text-Gray-600 typo-body3">
                    {event.period.useText}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex w-full flex-col items-center gap-24">
              <SectionTitle num="02" title="이벤트 참여 대상" />
              <div className="flex w-full flex-col gap-12">
                <p className="text-center font-500 text-Gray-black typo-body3">
                  KB국민 체크카드 온라인 신규 발급 회원
                  <br />
                  (신용, 기업, 선불카드 제외)
                </p>
                <div className="rounded-xlarge bg-Gray-25 px-20 py-12">
                  <div className="text-center text-Gray-600 typo-caption1">
                    <p>
                      *신규(발급)회원: 아래 중 한가지 충족하고
                      <br />
                      신규 발급 시점 체크카드 미보유 시 대상 인정
                    </p>

                    <ol className="mt-8 whitespace-pre-line pl-18 text-center">
                      {event.eligibility.conditions.map(condition => <li key={condition} className="list-inside list-decimal">
                          {condition}
                        </li>)}
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-full flex-col items-center gap-24">
              <SectionTitle num="03" title="이벤트 내용" />
              <div className="flex flex-col items-center gap-24">
                <div className="flex flex-col items-center gap-12">
                  <span className="rounded-pill bg-[#12b8f5] px-16 py-8 font-600 text-Gray-white typo-body3">
                    STEP 1
                  </span>
                  <p className="text-center font-500 text-Gray-black typo-body3">
                    본 이벤트 페이지를 통해
                    <br />
                    대상카드를 신규로 발급받고
                  </p>
                </div>

                <div className="flex flex-col items-center gap-12">
                  <span className="rounded-pill bg-[#12b8f5] px-16 py-8 font-600 text-Gray-white typo-body3">
                    STEP 2
                  </span>

                  {variantConfig.renderStep2(event.content)}
                </div>
              </div>
            </div>

            <Divider className="bg-Gray-100" />

            <div className="flex flex-col items-center gap-12">
              <SectionTitle num="04" title="대상 카드" />
              <div className="flex flex-col items-center gap-60">
                {event.cards.map(card => <div key={card.name} className="flex flex-col items-center gap-20 p-20">
                    <div className="flex flex-col items-center gap-8 text-center">
                      <p className="font-700 text-Gray-black typo-h6">
                        {card.name}
                      </p>
                      <p className="text-Gray-black typo-body3">
                        {card.subtitle}
                      </p>
                    </div>
                    <div className={card.images.length > 1 ? 'flex gap-20' : 'flex'}>
                      {card.images.map(img => <div key={img.alt}>
                          <Image src={img.src} alt={img.alt} width={200} height={126} className="object-contain" />
                        </div>)}
                    </div>
                    <div className="flex flex-wrap justify-center gap-8">
                      {card.tags.map(tag => <span key={tag} className="tracking-[-0.0175rem] text-Gray-600 typo-body4">
                          {tag}
                        </span>)}
                    </div>
                    <ul className="pl-20 tracking-[-0.0175rem] text-Gray-black typo-body4">
                      {card.benefits.map(benefit => <li key={benefit.label} className="list-disc">
                          <span className="text-Gray-600">
                            {benefit.label}{' '}
                          </span>
                          <span className="font-500">{benefit.highlight}</span>
                        </li>)}
                    </ul>
                    {card.monthlyUsageNote && <p className="tracking-[-0.0175rem] text-Gray-600 typo-body4">
                        {card.monthlyUsageNote}
                      </p>}
                    <CardApplyButton href={card.href} isMobile={isMobile} />
                  </div>)}
              </div>
            </div>

            <Divider className="bg-Gray-100" />

            <div className="flex flex-col items-center gap-24 text-center">
              <SectionTitle num="05" title="혜택 제공 일정" />
              <div className="font-500 text-Gray-black typo-body3">
                <p>{event.benefit.deadline}</p>
                <p>{event.benefit.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-Gray-100 px-30 py-40" aria-label="유의사항">
          <div className="mx-auto flex w-full max-w-[750px] flex-col gap-20">
            <h2 className="font-700 text-Gray-black typo-body2">
              이용 전 반드시 확인하세요!
            </h2>

            <ul className="pl-15 text-Gray-600 typo-caption2">
              <li className="list-disc">
                채널 內 이벤트 배너 통한 발급 필수(자동 응모 적용)
              </li>
              <li className="list-disc">
                KB국민 Youth Club 체크카드는 만18세~29세까지 가입이 가능합니다.
                <ul className="pl-15">
                  <li className="list-disc">
                    KB유스클럽 체크카드X유스클럽 프로모션{' '}
                    <a href={event.notices.youthClubPromoLink} target="_blank" rel="noopener noreferrer" className="underline transition-opacity duration-200 ease-out hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500 active:opacity-70">
                      [바로가기]
                    </a>
                  </li>
                </ul>
              </li>
              <li className="list-disc">
                KB국민 체크카드 신규회원 대상 타 이벤트 혜택과 중복적용 불가
                <ul className="pl-15">
                  <li className="list-disc">
                    중복 참여 시 발급월 기준 가장 앞선 신청 건 자동 적용
                  </li>
                  <li className="list-disc">
                    KB국민 체크카드 신규회원 이벤트 통합 본인 회원 기준 1인 1회
                    한하여 제공
                  </li>
                </ul>
              </li>
              <li className="list-disc">
                기존 체크카드 회원의 추가/교체 발급, 유효기간 미경과 해지 회원의
                재발급 시 대상 제외
              </li>
              <li className="list-disc">
                이용금액은 이용기간 종료 후 3영업일까지 정상 매입된 금액에
                한하며, 매출취소 및 KB국민카드 포인트리 충전금액 제외
              </li>
              <li className="list-disc">
                혜택 지급 전 해지, 거래정지, 연체 시 대상에서 제외
              </li>
              <li className="list-disc">
                실적·한도 조건 없이{' '}
                <a href={event.notices.baroDiscountLink} target="_blank" rel="noopener noreferrer" className="font-600 underline transition-opacity duration-200 ease-out hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500 active:opacity-70">
                  할인해주는 바로할인서비스 확인하기
                </a>
                <ul className="pl-15">
                  <li className="list-disc">
                    체크카드에 연결하여 선택한 가맹점에서 바코드로 결제 시 바로
                    할인적용
                  </li>
                </ul>
              </li>
              <li className="list-disc">연회비 : 없음</li>
              <li className="list-disc">
                계약을 체결하기 전에 상품설명서와 약관을 확인하시기 바랍니다.
              </li>
            </ul>

            <ul className="pl-15 font-700 text-Gray-600 typo-caption2">
              <li className="list-disc whitespace-pre-line">
                {`연체이자율: 회원별/이용상품별 정상이자율 + 3%p, 최고 연 20% 이내
※ 단, 연체발생시점에 정상이자율이 없는 경우 아래와 같이 적용함
- 일시불 거래 연체 시: 거래발생시점의 최소기간(2개월) 유이자 할부수수료율 적용
- 무이자할부 거래 연체 시: 거래발생시점의 동일한 할부계약 기간의 유이자할부수수료율 적용
- 그 외의 경우 : 정상이자율은 상법상 상사법정이율과 상호금융 가계자금대출금리* 중 높은 금리 적용
* 한국은행에서 매월 발표하는 가장 최근의 비은행 금융기관 가중평균대출금리(신규대출 기준)`}
              </li>
              <li className="list-disc">
                상환능력에 비해 신용카드 사용액이 과도할 경우, 귀하의
                개인신용평점이 하락할 수 있습니다.
              </li>
              <li className="list-disc">
                개인신용평점 하락 시 금융거래 관련된 불이익이 발생할 수
                있습니다.
              </li>
              <li className="list-disc">
                일정기간 원리금을 연체할 경우, 모든 원리금을 변제할 의무가
                발생할 수 있습니다.
              </li>
            </ul>

            <div className="text-Gray-600 typo-caption2">
              <p>
                ※ 본 행사는 KB 국민카드 영업정책 및 제휴업체 사정으로 변경 또는
                중단될 수 있습니다.
              </p>
              <ul className="pl-15">
                <li className="list-disc">
                  문의 : KB국민카드 고객센터(1588-1688)
                </li>
                <li className="list-disc">{event.notices.complianceText}</li>
              </ul>
            </div>
          </div>
        </section>
      </article>
    </CenteredDetailTemplate>;
};